import { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext, API } from "../App";
import { Trophy, Flame, Star, Sparkles, Activity, Gamepad2, Users as UsersIcon } from "lucide-react";

/**
 * Daily Challenges tab — shown inside MyAccountPanel.
 * Renders today's 3 personalized challenges (1 activity + 1 game + 1 social),
 * progress bars, claim buttons, XP/level/streak summary, and earned badges.
 */
const DailyChallengesTab = () => {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/challenges/today`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load daily challenges", err);
      toast.error("Could not load today's challenges");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const claim = async (challenge_id) => {
    setClaimingId(challenge_id);
    try {
      const res = await axios.post(`${API}/challenges/claim`, { challenge_id }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { reward_xp, reward_fchips, leveled_up, new_badges, level } = res.data;
      toast.success(`+${reward_xp} XP · +${reward_fchips} 🎰 f-chips`);
      if (leveled_up) {
        toast.success(`🎉 LEVEL UP! You are now Level ${level}!`, { duration: 6000 });
      }
      (new_badges || []).forEach(b => {
        toast.success(`🏅 New badge: ${b.icon} ${b.name}`, { duration: 5000 });
      });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't claim reward");
    } finally {
      setClaimingId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-zinc-500" data-testid="daily-challenges-loading">
        Loading today's challenges…
      </div>
    );
  }
  if (!data) return null;

  const { challenges = [], progress = {} } = data;
  const { xp = 0, level = 1, next_level_xp = 100, streak_days = 0, badges = [] } = progress;
  // XP progress within current level
  const xpFloor = Math.pow(Math.max(0, level - 1), 2) * 100;
  const xpRange = Math.max(1, next_level_xp - xpFloor);
  const xpInto = Math.max(0, Math.min(xpRange, xp - xpFloor));
  const xpPct = Math.round((xpInto / xpRange) * 100);

  const typeIcon = (t) => {
    if (t === "activity") return <Activity className="w-4 h-4 text-blue-600" />;
    if (t === "game") return <Gamepad2 className="w-4 h-4 text-purple-600" />;
    return <UsersIcon className="w-4 h-4 text-pink-600" />;
  };
  const typeBg = (t) => {
    if (t === "activity") return "from-blue-50 to-cyan-50 border-blue-400";
    if (t === "game") return "from-purple-50 to-fuchsia-50 border-purple-400";
    return "from-pink-50 to-rose-50 border-pink-400";
  };

  return (
    <div className="space-y-3" data-testid="daily-challenges-tab">
      {/* XP / Level / Streak summary card */}
      <div className="retro-window p-3 bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center text-white font-extrabold text-lg shadow" data-testid="user-level-badge">
              {level}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Level</div>
              <div className="text-sm font-bold">Level {level}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-center">
              <div className="flex items-center gap-1 text-orange-600 font-extrabold text-lg" data-testid="streak-counter">
                <Flame className="w-5 h-5" /> {streak_days}
              </div>
              <div className="text-[10px] text-zinc-500">Day Streak</div>
            </div>
            <div className="text-center">
              <div className="flex items-center gap-1 text-yellow-600 font-extrabold text-lg">
                <Star className="w-5 h-5" /> {xp}
              </div>
              <div className="text-[10px] text-zinc-500">Total XP</div>
            </div>
          </div>
        </div>
        {/* XP bar */}
        <div className="mt-2">
          <div className="flex justify-between text-[10px] text-zinc-600 mb-0.5">
            <span>{xp} XP</span>
            <span>{next_level_xp} XP — next level</span>
          </div>
          <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all"
              style={{ width: `${xpPct}%` }}
              data-testid="xp-progress-bar"
            />
          </div>
        </div>
        {/* Streak milestones hint */}
        {streak_days < 7 && (
          <p className="text-[10px] text-zinc-500 mt-2">
            🔥 Keep your streak alive — earn the <strong>Week Warrior</strong> badge at 7 days (+200 f-chips bonus).
          </p>
        )}
      </div>

      {/* Today's challenges */}
      <div>
        <h3 className="font-bold text-sm flex items-center gap-1 mb-2">
          <Trophy className="w-4 h-4 text-amber-600" /> Today's Challenges
        </h3>
        <div className="space-y-2">
          {challenges.map(ch => {
            const pct = Math.min(100, Math.round((ch.progress / ch.target) * 100));
            const can_claim = ch.completed && !ch.claimed;
            return (
              <div
                key={ch.id}
                className={`bg-gradient-to-r ${typeBg(ch.type)} border-l-4 rounded p-2.5`}
                data-testid={`challenge-card-${ch.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      {typeIcon(ch.type)}
                      <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{ch.type}</span>
                      {ch.completed && (
                        <span className="text-[10px] bg-green-600 text-white px-1.5 py-0.5 rounded-full font-bold ml-auto">
                          ✓ Done
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-bold text-zinc-800">{ch.title}</div>
                    <div className="text-xs text-zinc-600">{ch.description}</div>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-2">
                  <div className="flex items-center justify-between text-[10px] text-zinc-600 mb-0.5">
                    <span>{ch.progress} / {ch.target}</span>
                    <span className="font-bold text-amber-700">
                      +{ch.reward_xp} XP · +{ch.reward_fchips} 🎰
                    </span>
                  </div>
                  <div className="h-1.5 bg-white/70 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${ch.completed ? "bg-green-500" : "bg-amber-500"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                {/* Claim button */}
                {can_claim && (
                  <button
                    className="mt-2 w-full py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs font-bold rounded shadow disabled:opacity-50"
                    onClick={() => claim(ch.id)}
                    disabled={claimingId === ch.id}
                    data-testid={`claim-${ch.id}`}
                  >
                    {claimingId === ch.id ? "Claiming…" : `🎁 Claim +${ch.reward_xp} XP · +${ch.reward_fchips} 🎰`}
                  </button>
                )}
                {ch.claimed && (
                  <p className="mt-2 text-[10px] text-green-700 italic">✓ Reward claimed — see you tomorrow!</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Badges */}
      {badges.length > 0 && (
        <div className="retro-window p-3 bg-zinc-50">
          <h3 className="font-bold text-sm flex items-center gap-1 mb-2">
            <Sparkles className="w-4 h-4 text-purple-600" /> Earned Badges ({badges.length})
          </h3>
          <div className="grid grid-cols-4 gap-2" data-testid="badges-grid">
            {badges.map(b => (
              <div
                key={b.id}
                className="bg-white border border-zinc-200 rounded p-2 text-center shadow-sm"
                title={`${b.name} — earned ${new Date(b.earned_at).toLocaleDateString()}`}
              >
                <div className="text-2xl">{b.icon}</div>
                <div className="text-[10px] font-bold text-zinc-700 truncate">{b.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyChallengesTab;
